import { useMutation, useQueryClient } from '@tanstack/react-query';
import { stopDripConversation, type StopDripResult } from '@/lib/api';
import { conversationQueryKey } from './useConversation';

/**
 * useStopDrip — owner manually halts the GHL drip workflow on this contact
 * without pausing Shelby SMS replies. Posts to /pwa/conversations/:id/stop-drip
 * which writes `quote_followup_stop` + removes drip-trigger tags.
 *
 * Differs from useTakeover: this only stops drip; Shelby continues to
 * respond to inbound SMS. Used by the green drip pill on the conversation
 * header (bug 5 from Cowork hand-off 2026-06-02).
 *
 * Invalidates both the detail query (so is_workflow_active flips to false)
 * and the conversations list query (so the row pill clears too).
 */
export function useStopDrip(slug: string, conversationId: string) {
  const qc = useQueryClient();
  const key = conversationQueryKey(slug, conversationId);

  return useMutation<StopDripResult, Error, void>({
    mutationFn: () => stopDripConversation({ slug, conversationId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key });
      void qc.invalidateQueries({ queryKey: ['conversations', slug] });
    },
  });
}
